import type { Components } from '@/api/generated/api';
import type { AnalyticsUserProperties } from '@/services/analytics';
import { getGrowthPopupVariant } from '@/utils/growthPopupExperiment.utils';
import {
  ANONYMOUS_BILLING_USER_PROPERTIES,
  buildBillingAnalyticsUserProperties,
} from '@/services/analytics/billingState';
import {
  ANONYMOUS_USER_ROLE_USER_PROPERTIES,
  buildUserRoleAnalyticsUserProperties,
} from '@/services/analytics/userRole';

export type AnalyticsUserPropertiesContext = {
  billingStatus: Components.Schemas.BillingStatusDto | undefined;
  currentTenant: Components.Schemas.TenantDto | null;
  isLoggedIn: boolean;
  whoAmI: Components.Schemas.WhoAmIDataDto | null;
};

export type AnalyticsUserPropertiesContributor = (
  context: AnalyticsUserPropertiesContext,
) => AnalyticsUserProperties | null;

const ANONYMOUS_GROWTH_POPUP_USER_PROPERTIES: AnalyticsUserProperties = {
  growth_popup_participant: false,
  growth_popup_variant: 'unassigned',
};

const buildGrowthPopupUserProperties: AnalyticsUserPropertiesContributor = ({ isLoggedIn, whoAmI, currentTenant }) => {
  const variant =
    isLoggedIn && currentTenant && currentTenant.id === whoAmI?.currentTenantId ? getGrowthPopupVariant(whoAmI) : null;

  return {
    growth_popup_participant: variant != null,
    growth_popup_variant: variant ?? 'unassigned',
  };
};

export const NEUTRAL_ANALYTICS_USER_PROPERTIES: AnalyticsUserProperties = {
  ...ANONYMOUS_BILLING_USER_PROPERTIES,
  ...ANONYMOUS_USER_ROLE_USER_PROPERTIES,
  ...ANONYMOUS_GROWTH_POPUP_USER_PROPERTIES,
};

const analyticsUserPropertiesContributors: AnalyticsUserPropertiesContributor[] = [
  buildGrowthPopupUserProperties,
  (context) =>
    buildBillingAnalyticsUserProperties({
      billingStatus: context.billingStatus,
      hasCurrentTenant: context.currentTenant != null,
      isLoggedIn: context.isLoggedIn,
    }),
  buildUserRoleAnalyticsUserProperties,
];

export function buildAnalyticsUserProperties(context: AnalyticsUserPropertiesContext): AnalyticsUserProperties {
  if (context.isLoggedIn && !context.whoAmI) return { ...NEUTRAL_ANALYTICS_USER_PROPERTIES };

  const mergedProperties: AnalyticsUserProperties = {};

  for (const contributor of analyticsUserPropertiesContributors) {
    const properties = contributor(context);
    if (!properties) return { ...NEUTRAL_ANALYTICS_USER_PROPERTIES, ...mergedProperties };

    Object.assign(mergedProperties, properties);
  }

  return mergedProperties;
}
